import React, { useState } from 'react';
import INDIAN_STATES from '../../constants/indianStates';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Edit, Save, X, ArrowLeft, Plus, Bed, Utensils, Baby, PersonStanding, Gauge, FileText, HelpCircle } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// The Acting-Driver arrangement/requirement items store React-Native vector-icon
// NAMES (Ionicons / MaterialCommunityIcons) for the mobile app. The web dashboard
// can't render those, so map each name to its lucide-react equivalent.
const RN_ICON_MAP = {
  bed: Bed,
  restaurant: Utensils,
  'baby-carriage': Baby,
  'human-cane': PersonStanding,
  speedometer: Gauge,
  'document-text': FileText,
};

const DEFAULT_ARRANGEMENTS = [
  { key: 'accommodation', label: 'Driver Accommodation', desc: 'Driver stay arrangements will be borne by the customer.', icon: 'bed',        iconLib: 'Ionicons' },
  { key: 'food',          label: 'Driver Food Allowance', desc: 'Food allowance for driver',                               icon: 'restaurant', iconLib: 'Ionicons' },
];

const DEFAULT_SPECIAL_REQUIREMENTS = [
  { key: 'kids',          label: 'Children on Board',      desc: '',                      icon: 'baby-carriage', iconLib: 'MaterialCommunityIcons', type: 'toggle' },
  { key: 'elderly',       label: 'Elderly Passengers',     desc: '',                      icon: 'human-cane',    iconLib: 'MaterialCommunityIcons', type: 'toggle' },
  { key: 'maxSpeed',      label: 'Comfort Speed',          desc: 'Auto speed for driver', icon: 'speedometer',   iconLib: 'Ionicons',               type: 'number' },
  { key: 'otherRequests', label: 'Custom Request / Notes', desc: '',                      icon: 'document-text', iconLib: 'Ionicons',               type: 'text'   },
];

// All service items that can appear on the customer home screen
const ALL_SERVICES = [
  { key: 'auto',           label: 'Auto',           icon: '🛺' },
  { key: 'electric_auto',  label: 'Electric Auto',  icon: '⚡' },
  { key: 'schedule_trip',  label: 'Schedule',       icon: '📅' },
  { key: 'female_driver',  label: 'Female Driver',  icon: '👩' },
  { key: 'night_trip',     label: 'Night Ride',     icon: '🌙' },
  { key: 'mygarage',       label: 'My Garage',      icon: '🏠' },
  { key: 'acting_driver',  label: 'Acting Driver',  icon: '🚘' },
  { key: 'my_bookings',    label: 'My Bookings',    icon: '🧾' },
  { key: 'my_driver',      label: 'My Driver',      icon: '🧑‍✈️' },
];

const DEFAULT_HOME_CONFIG = [
  {
    key: 'services',
    sectionTitle: 'Choose Your Ride',
    showSectionTitle: false,
    items: ALL_SERVICES.map(s => ({ key: s.key, label: s.label, bgColor: '#f3f3f3ff', textColor: '#858585ff', borderColor: '#ffffffff' })),
  },
  {
    key: 'horizontal_banners',
    sectionTitle: 'Offers',
    showSectionTitle: false,
    items: [
      { id: 'bannerStops',  key: 'bannerStops',  label: 'Multi Stop' },
      { id: 'bannerFamily', key: 'bannerFamily', label: 'Family Ride' },
      { id: 'bannerAuto',   key: 'bannerAuto',   label: 'Female Driver' },
    ],
  },
];

const AppConfigDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [newState, setNewState] = useState('');
  const [uploadingField, setUploadingField] = useState(null);
  // Presigned display URLs for banners, keyed by field. Populated from the fetched
  // config and updated after a fresh upload. The persisted value is the object KEY.
  const [bannerPreviews, setBannerPreviews] = useState({});

  // Upload a banner → store its object KEY on the config, and its presigned URL for preview.
  const handleBannerUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingField(field);
    try {
      const fd = new FormData();
      fd.append('banner', file);
      const res = await api.post('/publicRidesAppConfig/upload-banner', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const key = res?.data?.key;
      if (key) {
        setFormData((prev) => ({ ...prev, [field]: key }));
        setBannerPreviews((prev) => ({ ...prev, [field]: res?.data?.url || '' }));
        toast.success('Banner uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingField(null);
      e.target.value = '';
    }
  };

  const { hasPermission } = useAuth();
  const canView = hasPermission('appconfig', 'view');
  const canWrite = hasPermission('appconfig', 'write');

  const { data, isLoading } = useQuery(
    ['appConfig', id],
    async () => {
      const response = await api.get(`/publicRidesAppConfig/${id}`);
      return response.data;
    },
    {
      enabled: canView
    }
  );

  const updateMutation = useMutation(
    async (updatedData) => {
      const response = await api.put(`/publicRidesAppConfig/edit/${id}`, updatedData);
      return response.data;
    },
    {
      onSuccess: (responseData) => {
        // Immediately update cache with saved data so UI reflects changes without waiting for refetch
        queryClient.setQueryData(['appConfig', id], responseData);
        toast.success('Configuration updated successfully');
        setIsEditMode(false);
      },
      onError: (error) => {
        const errorMsg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (typeof error?.response?.data === 'string' ? error?.response?.data : null) ||
          error?.message ||
          'Failed to update configuration';
        toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      }
    }
  );

  if (!canView) {
    return (
      <div className="empty-state">
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">
          You don't have permission to view app configuration details.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <div className="loading-spinner"></div>;
  }

  const config = data?.data;
  const isDriverApp = config?.APP?.toLowerCase().includes('driver');

  // Fields stored as { android, ios }. Configs created before that split hold a
  // bare scalar, which makes the per-platform inputs read `undefined` and vanish
  // from the form. Widen the scalar to both platforms so they render and save.
  const PLATFORM_FIELDS = ['APP_BUILD_NUMBER', 'APP_BUILD_VERSION', 'appLink'];

  const handleEdit = () => {
    const init = { ...config };

    PLATFORM_FIELDS.forEach((field) => {
      const value = init[field];
      if (value != null && typeof value !== 'object') {
        init[field] = { android: String(value), ios: String(value) };
      }
    });

    const dbArrangements = Array.isArray(init.ACTING_DRIVER_ARRANGEMENTS) ? init.ACTING_DRIVER_ARRANGEMENTS : [];
    const dbRequirements = Array.isArray(init.ACTING_DRIVER_SPECIAL_REQUIREMENTS) ? init.ACTING_DRIVER_SPECIAL_REQUIREMENTS : [];

    // Always rebuild from canonical defaults so icon/iconLib are always correct.
    // Only the `enabled` toggle state is preserved from the DB.
    init.ACTING_DRIVER_ARRANGEMENTS = DEFAULT_ARRANGEMENTS.map(def => {
      const saved = dbArrangements.find(d => d.key === def.key);
      return { ...def, enabled: saved ? saved.enabled !== false : true };
    });
    init.ACTING_DRIVER_SPECIAL_REQUIREMENTS = DEFAULT_SPECIAL_REQUIREMENTS.map(def => {
      const saved = dbRequirements.find(d => d.key === def.key);
      return { ...def, enabled: saved ? saved.enabled !== false : true };
    });

    setFormData(init);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setFormData({});
  };

  const handleSave = () => {
    // Validate HOME_SCREEN_CONFIG JSON
    if (formData.HOME_SCREEN_CONFIG) {
      try {
        const parsed = typeof formData.HOME_SCREEN_CONFIG === 'string' 
          ? JSON.parse(formData.HOME_SCREEN_CONFIG) 
          : formData.HOME_SCREEN_CONFIG;
        
        if (!Array.isArray(parsed)) {
          toast.error('HOME_SCREEN_CONFIG must be a valid JSON array');
          return;
        }
        
        formData.HOME_SCREEN_CONFIG = parsed;
      } catch (error) {
        toast.error('Invalid JSON format for HOME_SCREEN_CONFIG');
        return;
      }
    }

    // Convert STATES array of strings back to array of objects
    if (formData.STATES && Array.isArray(formData.STATES) && formData.STATES.length > 0 && typeof formData.STATES[0] === 'string') {
      formData.STATES = formData.STATES.map((state, index) => ({
        id: index + 1,
        value: state,
        label: state
      }));
    }
    
    // Vehicles per driver: blank (default) or a whole number 1–20.
    const maxVehicles = formData.MAX_VEHICLES_PER_DRIVER;
    if (maxVehicles !== undefined && maxVehicles !== null && maxVehicles !== ''
        && !(Number.isInteger(Number(maxVehicles)) && Number(maxVehicles) >= 1 && Number(maxVehicles) <= 20)) {
      toast.error('Vehicles per driver must be a whole number from 1 to 20');
      return;
    }

    updateMutation.mutate(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, child, value) => {
    setFormData(prev => {
      // Spreading a scalar would yield character-indexed keys, so only carry the
      // sibling platform over when the current value is already an object.
      const current = typeof prev[parent] === 'object' && prev[parent] !== null ? prev[parent] : {};
      return { ...prev, [parent]: { ...current, [child]: value } };
    });
  };

  // FORCE_UPDATE on the customer config is TWO levels deep — {ANDROID: {VERSION,
  // BUILD_NUMBER}, IOS: {...}} — one level deeper than handleNestedChange handles.
  // Same immutable-spread shape, just carrying the middle key's siblings too.
  const handleDeepNestedChange = (parent, midKey, child, value) => {
    setFormData(prev => {
      const parentObj = typeof prev[parent] === 'object' && prev[parent] !== null ? prev[parent] : {};
      const midObj = typeof parentObj[midKey] === 'object' && parentObj[midKey] !== null ? parentObj[midKey] : {};
      return { ...prev, [parent]: { ...parentObj, [midKey]: { ...midObj, [child]: value } } };
    });
  };

  const renderField = (label, field, type = 'text') => {
    const value = isEditMode ? formData[field] : config?.[field];
    if (type !== 'checkbox' && (value === undefined || value === null)) return null;
    if (type === 'checkbox' && config?.[field] === undefined) return null;
    
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        {isEditMode ? (
          type === 'checkbox' ? (
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => handleChange(field, e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">{value ? 'Yes' : 'No'}</span>
            </div>
          ) : type === 'number' ? (
            <input
              type="number"
              value={value || ''}
              onChange={(e) => handleChange(field, Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          ) : (
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleChange(field, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          )
        ) : (
          <p className="text-gray-900 px-3 py-2 bg-gray-50 rounded-md">
            {type === 'checkbox' ? (value ? 'Yes' : 'No') : (value || 'N/A')}
          </p>
        )}
      </div>
    );
  };

  const renderNestedField = (label, parent, child) => {
    const value = isEditMode ? formData[parent]?.[child] : config?.[parent]?.[child];
    if (value === undefined || value === null) return null;
    
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        {isEditMode ? (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleNestedChange(parent, child, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        ) : (
          <p className="text-gray-900 px-3 py-2 bg-gray-50 rounded-md">{value || 'N/A'}</p>
        )}
      </div>
    );
  };

  /**
   * FORCE_UPDATE.{ANDROID,IOS}.{VERSION,BUILD_NUMBER} on the customer app config —
   * two levels deep, unlike the driver config's flat FORCE_UPDATE_VERSION field.
   * Deliberately does NOT hide on undefined the way renderNestedField does: that
   * guard is right for a per-platform build number/link a config either has or
   * doesn't, but a force-update field an admin needs to be ABLE to set even on a
   * config that has never had one — hiding it there would make it permanently
   * unreachable from this form, same trap that sent the mongo shell for it.
   */
  const renderDeepNestedField = (label, parent, midKey, child, type = 'text') => {
    const value = isEditMode ? formData[parent]?.[midKey]?.[child] : config?.[parent]?.[midKey]?.[child];

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        {isEditMode ? (
          <input
            type={type === 'number' ? 'number' : 'text'}
            value={value ?? ''}
            onChange={(e) => handleDeepNestedChange(parent, midKey, child, type === 'number' ? Number(e.target.value) : e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        ) : (
          <p className="text-gray-900 px-3 py-2 bg-gray-50 rounded-md">{value ?? 'N/A'}</p>
        )}
      </div>
    );
  };

  const ALL_BANNERS = [
    { key: 'bannerStops',  id: 'bannerStops',  label: 'Multi Stop',     icon: '📍', description: 'Add multiple stops to a ride' },
    { key: 'bannerFamily', id: 'bannerFamily', label: 'Family Ride',    icon: '👨‍👩‍👧', description: 'Book a ride for the whole family' },
    { key: 'bannerAuto',   id: 'bannerAuto',   label: 'Female Driver',  icon: '👩‍✈️', description: 'Request a female driver' },
  ];

  const renderHomeScreenConfig = () => {
    const rawConfig = isEditMode ? formData.HOME_SCREEN_CONFIG : config?.HOME_SCREEN_CONFIG;
    const homeConfig = Array.isArray(rawConfig) ? rawConfig : DEFAULT_HOME_CONFIG;

    const servicesSection = homeConfig.find(s => s.key === 'services');
    const enabledServiceKeys = (servicesSection?.items || []).map(i => i.key);

    const bannersSection = homeConfig.find(s => s.key === 'horizontal_banners');
    // Fall back to `id` — the "Ride with us" rows already live in production were
    // seeded with only an `id` (no `key`), so reading `.key` alone made every one
    // of them look disabled here even though the app was correctly showing them
    // (it resolves banner images by `id`, never `key`). The toggle below writes
    // both fields for anything it adds, so this fallback only matters for those
    // pre-existing rows.
    const enabledBannerKeys = (bannersSection?.items || DEFAULT_HOME_CONFIG.find(s => s.key === 'horizontal_banners')?.items || []).map(i => i.key || i.id);

    const toggleSection = (sectionKey, itemKey, allItems, defaultItem) => {
      const currentConfig = Array.isArray(formData.HOME_SCREEN_CONFIG)
        ? JSON.parse(JSON.stringify(formData.HOME_SCREEN_CONFIG))
        : JSON.parse(JSON.stringify(DEFAULT_HOME_CONFIG));

      let sIdx = currentConfig.findIndex(s => s.key === sectionKey);
      if (sIdx === -1) {
        const defaultSection = DEFAULT_HOME_CONFIG.find(s => s.key === sectionKey);
        currentConfig.push(defaultSection ? JSON.parse(JSON.stringify(defaultSection)) : { key: sectionKey, items: [] });
        sIdx = currentConfig.length - 1;
      }

      const currentItems = [...(currentConfig[sIdx].items || [])];
      // Same `key`-or-`id` fallback as enabledBannerKeys above, so toggling off a
      // pre-existing `id`-only row actually finds and removes it instead of
      // silently adding a second, `key`-only copy of the same banner.
      const existingIdx = currentItems.findIndex(i => (i.key || i.id) === itemKey);

      if (existingIdx !== -1) {
        currentItems.splice(existingIdx, 1);
      } else {
        const meta = allItems.find(s => s.key === itemKey);
        if (meta) currentItems.push(defaultItem(meta));
      }

      currentConfig[sIdx] = { ...currentConfig[sIdx], items: currentItems };
      handleChange('HOME_SCREEN_CONFIG', currentConfig);
    };

    const toggleService = (key) => toggleSection(
      'services', key, ALL_SERVICES,
      (meta) => ({ key: meta.key, label: meta.label, bgColor: '#f3f3f3ff', textColor: '#858585ff', borderColor: '#ffffffff' })
    );

    const toggleBanner = (key) => toggleSection(
      'horizontal_banners', key, ALL_BANNERS,
      (meta) => ({ id: meta.id, key: meta.key, label: meta.label })
    );

    const ToggleSwitch = ({ on }) => (
      <div className={`w-10 h-6 rounded-full relative transition-colors flex-shrink-0 ${on ? 'bg-green-500' : 'bg-gray-300'}`}>
        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'right-1' : 'left-1'}`} />
      </div>
    );

    return (
      <div className="mb-6 space-y-4">

        {/* ── Vehicle Services ── */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Home Screen — Ride Options</label>
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-3">Toggle vehicle types shown on the customer home screen.</p>
            <div className="grid grid-cols-2 gap-3">
              {ALL_SERVICES.map(service => {
                const on = enabledServiceKeys.includes(service.key);
                return (
                  <div
                    key={service.key}
                    onClick={() => isEditMode && toggleService(service.key)}
                    className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all select-none
                      ${on ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'}
                      ${isEditMode ? 'cursor-pointer hover:border-blue-400' : 'cursor-default opacity-80'}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{service.icon}</span>
                      <span className="text-sm font-medium text-gray-800">{service.label}</span>
                    </div>
                    <ToggleSwitch on={on} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Promotional Banners ── */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Home Screen — Promotional Banners</label>
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-3">Toggle banners shown in the horizontal scroll section.</p>
            <div className="space-y-2">
              {ALL_BANNERS.map(banner => {
                const on = enabledBannerKeys.includes(banner.key);
                return (
                  <div
                    key={banner.key}
                    onClick={() => isEditMode && toggleBanner(banner.key)}
                    className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all select-none
                      ${on ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'}
                      ${isEditMode ? 'cursor-pointer hover:border-blue-500' : 'cursor-default opacity-80'}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{banner.icon}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{banner.label}</p>
                        <p className="text-xs text-gray-500">{banner.description}</p>
                      </div>
                    </div>
                    <div className={`w-10 h-6 rounded-full relative flex-shrink-0 transition-colors ${on ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'right-1' : 'left-1'}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {!isEditMode && (
          <p className="text-xs text-gray-400 text-center">Click Edit to modify these settings</p>
        )}
      </div>
    );
  };

  const renderJsonField = (label, field) => {
    const value = isEditMode ? formData[field] : config?.[field];
    if (value === undefined || value === null) return null;
    const displayValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : value;
    
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        {isEditMode ? (
          <textarea
            value={displayValue || ''}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
            rows="10"
            placeholder='[{"key": "value"}]'
          />
        ) : (
          <pre className="text-gray-900 px-3 py-2 bg-gray-50 rounded-md overflow-x-auto text-sm">
            {displayValue || 'N/A'}
          </pre>
        )}
      </div>
    );
  };

  const renderArrayField = (label, field) => {
    const value = isEditMode ? formData[field] : config?.[field];
    if (!value || !Array.isArray(value) || value.length === 0) return null;
    
    const displayValue = value.map(item => 
      typeof item === 'object' ? (item.label || item.value || item.name || '') : item
    ).filter(Boolean).join(', ');
    
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        {isEditMode ? (
          <input
            type="text"
            value={displayValue}
            onChange={(e) => handleChange(field, e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter comma-separated values"
          />
        ) : (
          <p className="text-gray-900 px-3 py-2 bg-gray-50 rounded-md">
            {displayValue}
          </p>
        )}
      </div>
    );
  };

  /*
      Normal-ride drivers can keep more than one vehicle (app: My Vehicles) and
      switch between them with admin approval. This caps how many, the current one
      included. Always shown for a driver config, even when unset, unlike
      renderField which hides a field the document does not have yet.
  */
  const renderVehicleLimitField = () => {
    const value = isEditMode ? formData.MAX_VEHICLES_PER_DRIVER : config?.MAX_VEHICLES_PER_DRIVER;
    const isSet = value !== undefined && value !== null && value !== '';
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Normal Ride: Vehicles per Driver</label>
        <p className="text-xs text-gray-500 mb-2">
          Total vehicles a driver can keep in the app's My Vehicles, including the one they drive now. Leave blank to use the default (2).
        </p>
        {isEditMode ? (
          <input
            type="number"
            min={1}
            max={20}
            step={1}
            value={isSet ? value : ''}
            placeholder="2 (default)"
            onChange={(e) => handleChange('MAX_VEHICLES_PER_DRIVER', e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        ) : (
          <p className="text-sm text-gray-900">
            {isSet ? `${value} vehicle${Number(value) === 1 ? '' : 's'}` : <span className="text-gray-500">Not set — default 2</span>}
          </p>
        )}
      </div>
    );
  };

  const renderActingDriverConfig = () => {
    if (isDriverApp) return null;

    const rawArrangements = isEditMode
      ? (formData.ACTING_DRIVER_ARRANGEMENTS ?? config?.ACTING_DRIVER_ARRANGEMENTS ?? DEFAULT_ARRANGEMENTS.map(a => ({ ...a, enabled: true })))
      : (config?.ACTING_DRIVER_ARRANGEMENTS ?? DEFAULT_ARRANGEMENTS.map(a => ({ ...a, enabled: true })));

    const rawRequirements = isEditMode
      ? (formData.ACTING_DRIVER_SPECIAL_REQUIREMENTS ?? config?.ACTING_DRIVER_SPECIAL_REQUIREMENTS ?? DEFAULT_SPECIAL_REQUIREMENTS.map(r => ({ ...r, enabled: true })))
      : (config?.ACTING_DRIVER_SPECIAL_REQUIREMENTS ?? DEFAULT_SPECIAL_REQUIREMENTS.map(r => ({ ...r, enabled: true })));

    const toggleItem = (field, key) => {
      if (!isEditMode) return;
      const current = field === 'ACTING_DRIVER_ARRANGEMENTS' ? [...rawArrangements] : [...rawRequirements];
      const idx = current.findIndex(i => i.key === key);
      if (idx === -1) return;
      const updated = [...current];
      updated[idx] = { ...updated[idx], enabled: !updated[idx].enabled };
      handleChange(field, updated);
    };

    const ToggleSwitch = ({ on }) => (
      <div className={`w-10 h-6 rounded-full relative transition-colors flex-shrink-0 ${on ? 'bg-purple-500' : 'bg-gray-300'}`}>
        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'right-1' : 'left-1'}`} />
      </div>
    );

    const renderItemList = (items, field) => (
      <div className="space-y-2">
        {items.map(item => (
          <div
            key={item.key}
            onClick={() => toggleItem(field, item.key)}
            className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all select-none
              ${item.enabled ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-white'}
              ${isEditMode ? 'cursor-pointer hover:border-purple-500' : 'cursor-default opacity-80'}`}
          >
            <div className="flex items-center gap-3">
              {(() => {
                const IconComp = RN_ICON_MAP[item.icon];
                if (IconComp) return <IconComp className="w-5 h-5 text-purple-500 flex-shrink-0" />;
                // Emoji or unknown name → show as-is (falls back to a generic icon for unmapped names).
                return item.icon && /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(item.icon)
                  ? <span className="text-xl">{item.icon}</span>
                  : <HelpCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />;
              })()}
              <div>
                <p className="text-sm font-medium text-gray-800">{item.label}</p>
                {item.desc && <p className="text-xs text-gray-500">{item.desc}</p>}
                {item.type && item.type !== 'toggle' && (
                  <span className="text-xs text-purple-500 font-medium capitalize">{item.type} input</span>
                )}
              </div>
            </div>
            <ToggleSwitch on={!!item.enabled} />
          </div>
        ))}
      </div>
    );

    return (
      <div className="mb-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Driver Arrangements</label>
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-3">Toggle which arrangement options are shown on the Acting Driver booking screen.</p>
            {renderItemList(rawArrangements, 'ACTING_DRIVER_ARRANGEMENTS')}
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Special Requirements</label>
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-3">Toggle which special requirement options are shown on the Acting Driver booking screen.</p>
            {renderItemList(rawRequirements, 'ACTING_DRIVER_SPECIAL_REQUIREMENTS')}
          </div>
        </div>
      </div>
    );
  };

  const renderStatesField = () => {
    const states = isEditMode ? formData.STATES || [] : config?.STATES || [];
    
    const handleAddState = () => {
      if (!newState.trim()) {
        toast.error('Please enter a state name');
        return;
      }
      const maxId = states.length > 0 ? Math.max(...states.map(s => s.id || 0)) : 0;
      const newStateObj = { id: maxId + 1, value: newState.trim(), label: newState.trim() };
      handleChange('STATES', [...states, newStateObj]);
      setNewState('');
    };

    const handleRemoveState = (id) => {
      handleChange('STATES', states.filter(s => s.id !== id));
    };

    if (!isEditMode && states.length === 0) return null;

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">States</label>
        {isEditMode ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={newState}
                onChange={(e) => setNewState(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddState()}
                placeholder="Enter state name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={handleAddState}
                className="btn btn-primary flex items-center gap-2 whitespace-nowrap"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>
            {states.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                {states.map((state) => (
                  <span
                    key={state.id}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                  >
                    {state.label || state.value}
                    <button
                      type="button"
                      onClick={() => handleRemoveState(state.id)}
                      className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-md border border-gray-200">
            {states.map((state) => (
              <span
                key={state.id}
                className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium"
              >
                {state.label || state.value}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/config')}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="page-title">App Configuration Details</h1>
                <p className="page-subtitle">{config?.APP || 'Configuration'}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              {isEditMode ? (
                <>
                  <button
                    onClick={handleCancel}
                    className="btn btn-outline"
                    disabled={updateMutation.isLoading}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn btn-primary"
                    disabled={updateMutation.isLoading}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {updateMutation.isLoading ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                <button onClick={handleEdit} className="btn btn-primary">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-6">
            {renderField('App', 'APP')}
            {renderField('Region Code', 'regionCode')}
            {/* State — scopes this config state-wise. Always shown (even on older
                configs missing the field) so it can be set. */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
              {isEditMode ? (
                <select
                  value={formData.state ?? config?.state ?? 'DEFAULT'}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="DEFAULT">DEFAULT (fallback)</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <p className="text-gray-900">{config?.state || 'DEFAULT'}</p>
              )}
            </div>

            {/* Home-screen banners — one for non-subscribers (upsell), one for active subscribers. */}
            {[
              {
                field: 'SUBSCRIPTION_BANNER',
                label: 'Subscription Banner (non-subscribers)',
                desc: 'Shown on the home screen to users who are NOT subscribed. Tapping it opens the NOT Premium screen.',
              },
              {
                field: 'SUBSCRIBED_BANNER',
                label: 'Subscribed Banner (active subscribers)',
                desc: 'Shown on the home screen to active NOT Premium subscribers instead of the upsell banner.',
              },
            ].map(({ field, label, desc }) => {
              // The stored value is the object KEY; display uses a presigned preview URL.
              const bannerKey = isEditMode ? (formData[field] ?? config?.[field]) : config?.[field];
              const previewUrl = bannerPreviews[field] || config?.bannerPreviews?.[field];
              const uploading = uploadingField === field;
              return (
                <div className="mb-4" key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                  <p className="text-xs text-gray-400 mb-2">{desc}</p>
                  {bannerKey && previewUrl ? (
                    <img src={previewUrl} alt={label} className="w-full max-w-md rounded-lg border border-gray-200 mb-2" />
                  ) : bannerKey ? (
                    <p className="text-sm text-gray-400 mb-2">Banner set (preview unavailable).</p>
                  ) : (
                    <p className="text-sm text-gray-400 mb-2">No banner set.</p>
                  )}
                  {isEditMode && (
                    <div className="flex items-center gap-3">
                      <label className={`btn btn-outline btn-sm cursor-pointer ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
                        {uploading ? 'Uploading…' : (bannerKey ? 'Replace image' : 'Upload image')}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleBannerUpload(e, field)} disabled={uploading} />
                      </label>
                      {bannerKey && (
                        <button
                          type="button"
                          className="text-sm text-red-600 hover:underline"
                          onClick={() => handleChange(field, '')}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Regional office — show linked office name (read-only, managed at creation) */}
            {config?.regionalOffice && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Regional Office</label>
                <p className="text-gray-900 px-3 py-2 bg-gray-50 rounded-md flex items-center gap-2">
                  🏢 {config.regionalOffice.name}
                  {config.regionalOffice.isActive === false && (
                    <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-600 rounded ml-1">Inactive</span>
                  )}
                </p>
              </div>
            )}
            {renderField('App Name', 'APP_NAME')}
            {renderField('Company Name', 'COMPANYNAME')}
            {renderField('Company Address', 'COMPANYADDRESS')}
            {renderField('Company Phone', 'COMPANYPHONE')}
            {renderField('Company Email', 'COMPANYEMAIL')}
            {renderField('App Version', 'APP_VERSION')}
            {renderField('Payment Methods', 'PAYMENT_METHODS')}
            {renderNestedField('Android Build Number', 'APP_BUILD_NUMBER', 'android')}
            {renderNestedField('iOS Build Number', 'APP_BUILD_NUMBER', 'ios')}
            {renderNestedField('Android Build Version', 'APP_BUILD_VERSION', 'android')}
            {renderNestedField('iOS Build Version', 'APP_BUILD_VERSION', 'ios')}
            {renderNestedField('Android App Link', 'appLink', 'android')}
            {renderNestedField('iOS App Link', 'appLink', 'ios')}
            {renderField('Show Wait Price Info', 'SHOW_WAIT_PRICE_INFO', 'checkbox')}
            {renderField('Default Wait Time (min)', 'DEFAULT_WAIT_TIME', 'number')}
            {renderField('Wait Time Charges Per Minute', 'WAIT_TIME_CHARGES_PER_MINUTE', 'number')}
            {renderField('Max Wait Time (min)', 'MAX_WAIT_TIME', 'number')}
            {renderField('Show Nearby Driver', 'SHOW_NEARBY_DRIVER', 'checkbox')}
            {renderField('Nearby Driver Radius (m)', 'SHOW_NEARBY_DRIVER_RADIUS', 'number')}
            {renderField('Driver Location Update Interval (ms)', 'DRIVER_LOCATION_UPDATE_INTERVAL', 'number')}
            {renderField('Min Trip Distance (m)', 'MIN_TRIP_DISTANCE_METER', 'number')}
            {renderField('Customer Cancel Penalty', 'CUSTOMER_CANCEL_PENALTY', 'checkbox')}
            {renderField('Cancel Penalty Limit', 'CUSTOMER_CANCEL_PENALTY_LIMIT', 'number')}
            {renderField('In-App Review Always', 'IN_APP_REVIEW_ALWAYS', 'checkbox')}
            {renderField('Required Rating for Review', 'IN_APP_REVIEW_REQUIRED_RATING', 'number')}
            {!isDriverApp && (() => {
              // enabledServices is the actual source of truth (see the model's own
              // doc comment on that field) — the server's pre-save hook derives
              // actingDriverEnabled FROM it on every save, unconditionally. This
              // toggle used to write only actingDriverEnabled directly, which made
              // it a no-op the moment you hit Save: the hook immediately overwrote
              // it back to whatever enabledServices already said, since nothing had
              // touched that array. Reading the array first (falling back to the
              // legacy flag only for rows saved before enabledServices existed)
              // keeps the displayed state honest either way.
              const currentServices = Array.isArray(isEditMode ? formData.enabledServices : config?.enabledServices)
                ? (isEditMode ? formData.enabledServices : config.enabledServices)
                : null;
              const actingOn = currentServices
                ? currentServices.includes('ACTING')
                : (isEditMode
                  ? (formData.actingDriverEnabled ?? config?.actingDriverEnabled ?? false)
                  : (config?.actingDriverEnabled ?? false));
              const toggleActing = () => {
                if (!isEditMode) return;
                const base = currentServices || ['RIDE'];
                const nextServices = actingOn
                  ? base.filter(s => s !== 'ACTING')
                  : [...new Set([...base, 'ACTING'])];
                handleChange('enabledServices', nextServices);
                // Set alongside for immediate UI feedback; the server derives and
                // confirms this from enabledServices again on save regardless.
                handleChange('actingDriverEnabled', !actingOn);
              };
              return (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Acting Driver Banner
                  </label>
                  <div
                    onClick={toggleActing}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all select-none
                      ${actingOn ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-gray-50'}
                      ${isEditMode ? 'cursor-pointer hover:border-purple-500' : 'cursor-default'}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🚘</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {actingOn ? 'Enabled' : 'Disabled'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Show "Hire an Acting Driver" banner &amp; booking flow on customer home screen
                        </p>
                      </div>
                    </div>
                    <div className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${actingOn ? 'bg-purple-500' : 'bg-gray-300'}`}>
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-md transition-all ${actingOn ? 'right-1' : 'left-1'}`} />
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Where the customer home screen's "Where do you want to go?" search
                leads. Customer app only — the driver app has no such search. */}
            {!isDriverApp && (() => {
              const OPTIONS = [
                { value: 'AUTO', label: 'Automatic (recommended)',
                  desc: 'Acting-only regions open Acting Driver booking; every other region opens the ride planner.' },
                { value: 'RIDE', label: 'Always Normal Ride',
                  desc: 'The search box always opens the normal ride planner.' },
                { value: 'ACTING', label: 'Always Acting Driver',
                  desc: 'The search box always opens Acting Driver booking.' },
              ];
              const current = String(
                (isEditMode ? formData.HOME_SEARCH_TARGET : config?.HOME_SEARCH_TARGET)
                ?? config?.HOME_SEARCH_TARGET ?? 'AUTO',
              ).toUpperCase();
              return (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Home Search Opens
                  </label>
                  <div className="space-y-2">
                    {OPTIONS.map((opt) => {
                      const selected = current === opt.value;
                      return (
                        <div
                          key={opt.value}
                          onClick={() => isEditMode && handleChange('HOME_SEARCH_TARGET', opt.value)}
                          className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all select-none
                            ${selected ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-gray-50'}
                            ${isEditMode ? 'cursor-pointer hover:border-purple-500' : 'cursor-default'}`}
                        >
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
                            <p className="text-xs text-gray-500">{opt.desc}</p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? 'border-purple-500' : 'border-gray-300'}`}>
                            {selected && <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            {!isDriverApp && renderHomeScreenConfig()}
            {renderActingDriverConfig()}
            {renderStatesField()}
            {isDriverApp ? (
              <>
                {renderField('Force Update Version', 'FORCE_UPDATE_VERSION')}
                {renderField('Force Update Build Number', 'FORCE_UPDATE_BUILD_NUMBER')}
                {renderField('Immediate Update', 'IMMEDIATE_UPDATE', 'checkbox')}
                {renderVehicleLimitField()}
              </>
            ) : (
              /*
                  The customer app reads FORCE_UPDATE.{ANDROID,IOS}.{VERSION,
                  BUILD_NUMBER} (see checkUpdateStatus in the app's UpdateChecker.js)
                  — a different, nested shape from the driver config's flat fields
                  above. This form never had inputs for it at all: the only thing
                  editable near it was APP_BUILD_VERSION (android/ios), a DIFFERENT,
                  unused field nothing in the app ever reads — which is exactly what
                  someone reasonably (but incorrectly) edited trying to trigger a
                  force update here, since it was the only per-platform version
                  field this form showed for a customer config.
              */
              <>
                {renderDeepNestedField('Force Update Version (Android)', 'FORCE_UPDATE', 'ANDROID', 'VERSION')}
                {renderDeepNestedField('Force Update Build Number (Android)', 'FORCE_UPDATE', 'ANDROID', 'BUILD_NUMBER', 'number')}
                {renderDeepNestedField('Force Update Version (iOS)', 'FORCE_UPDATE', 'IOS', 'VERSION')}
                {renderDeepNestedField('Force Update Build Number (iOS)', 'FORCE_UPDATE', 'IOS', 'BUILD_NUMBER', 'number')}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppConfigDetails;
